'use client';

import { Cloudinary } from '@cloudinary/url-gen';
import { format } from '@cloudinary/url-gen/actions/delivery';
import { getCloudinaryCloudName } from './cloudinary';

let cloudinaryInstance: Cloudinary | null = null;
let cloudNameCache: string | null = null;
let initPromise: Promise<Cloudinary | null> | null = null;

/**
 * Initialize Cloudinary instance with cloud name - cached and optimized
 */
async function initCloudinary(): Promise<Cloudinary | null> {
  // Return existing instance if available
  if (cloudinaryInstance && cloudNameCache) {
    return cloudinaryInstance;
  }

  // Return existing promise if initialization is in progress
  if (initPromise) {
    return initPromise;
  }

  // Start initialization
  initPromise = (async () => {
    try {
      const { cloudName } = await getCloudinaryCloudName();
      if (!cloudName) {
        console.error('Cloudinary cloud name not configured');
        return null;
      }

      cloudNameCache = cloudName;
      cloudinaryInstance = new Cloudinary({ cloud: { cloudName } });
      return cloudinaryInstance;
    } catch (error) {
      return null;
    } finally {
      // Clear the promise so future calls can retry if needed
      initPromise = null;
    }
  })();

  return initPromise;
}

/**
 * Create Cloudinary image object with WebP delivery format
 * @param publicId - The public ID of the image (without file extension)
 * @returns Cloudinary image object or null if failed
 */
export async function createCloudinaryImage(publicId: string) {
  if (!publicId?.trim()) {
    return null;
  }

  const cld = await initCloudinary();
  if (!cld) {
    return null;
  }

  try {
    const img = cld.image(publicId.trim());
    // Apply WebP format for faster loading
    img.delivery(format('webp'));
    return img;
  } catch (error) {
    console.error('Failed to create Cloudinary image:', error);
    return null;
  }
}

/**
 * Batch create Cloudinary image objects for multiple public IDs - optimized for speed
 * @param publicIds - Array of public IDs
 * @returns Array of Cloudinary image objects (null for failed ones)
 */
export async function createCloudinaryImages(publicIds: string[]) {
  if (publicIds.length === 0) {
    return [];
  }

  const cld = await initCloudinary();
  if (!cld) {
    return publicIds.map(() => null);
  }

  // Create all images synchronously once Cloudinary is initialized
  return publicIds.map(publicId => {
    if (!publicId?.trim()) {
      return null;
    }

    try {
      const img = cld.image(publicId.trim());
      // Apply WebP format for faster loading
      img.delivery(format('webp'));
      return img;
    } catch (error) {
      console.error(`Failed to create Cloudinary image for ${publicId}:`, error);
      return null;
    }
  });
}

/**
 * Synchronously create Cloudinary image if instance is already initialized
 * This is much faster for subsequent calls
 */
export function createCloudinaryImageSync(publicId: string) {
  if (!publicId?.trim() || !cloudinaryInstance) {
    return null;
  }

  try {
    const img = cloudinaryInstance.image(publicId.trim());
    img.delivery(format('webp'));
    return img;
  } catch (error) {
    console.error(`Failed to create Cloudinary image for ${publicId}:`, error);
    return null;
  }
}

/**
 * Check if Cloudinary is ready for synchronous operations
 */
export function isCloudinaryReady(): boolean {
  return cloudinaryInstance !== null;
}

/**
 * Preload/warm up Cloudinary instance - call this early to reduce initialization time
 */
export function preloadCloudinary(): Promise<Cloudinary | null> {
  return initCloudinary();
}

/**
 * Initialize Cloudinary with preloaded configuration (much faster)
 * Use this when you already have the cloud name from server-side preload
 */
export function initCloudinaryWithPreloadedConfig(cloudName: string): Cloudinary | null {
  if (cloudinaryInstance) {
    return cloudinaryInstance;
  }

  try {
    cloudNameCache = cloudName;
    cloudinaryInstance = new Cloudinary({ cloud: { cloudName } });
    return cloudinaryInstance;
  } catch (error) {
    console.error('Failed to initialize Cloudinary with preloaded config:', error);
    return null;
  }
}

/**
 * Batch create Cloudinary images using preloaded configuration
 * This is the fastest method when cloud name is already known
 */
export function createCloudinaryImagesWithPreload(
  cloudName: string, 
  imageData: Array<{ avatarId: string; publicId: string }>
): Record<string, any> {
  // Initialize Cloudinary instantly with preloaded config
  const cld = initCloudinaryWithPreloadedConfig(cloudName);
  if (!cld) {
    console.error('Failed to initialize Cloudinary with preloaded config');
    return {};
  }

  // Create all images synchronously
  const cloudinaryImages: Record<string, any> = {};
  
  imageData.forEach(({ avatarId, publicId }) => {
    try {
      const img = cld.image(publicId.trim());
      img.delivery(format('webp'));
      cloudinaryImages[avatarId] = img;
    } catch (error) {
      console.error(`Failed to create Cloudinary image for ${publicId}:`, error);
    }
  });

  return cloudinaryImages;
}
